import 'chart.js/auto';
import BlockUi from '@availity/block-ui';
import { ChartOptions, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment/moment';
import React from 'react';
import { Line } from 'react-chartjs-2';
import { CSVLink } from 'react-csv';
import { connect } from 'react-redux';
import { Button, Dropdown, Form, Grid, Header, Icon, Label, Segment } from 'semantic-ui-react';
import { getRouteMapGeometryByRoutes, getRoutesPairsWithLongName } from '../../../actions/gtfsStaticActions';
import { getAverageWeekdayBoardingSummary, getAverageWeekdayBoardingTimeline, getRidershipStopActivityByRoutes } from '../../../actions/ridershipActions';
import { AppState } from '../../../reducers';
import { getSelectedOrDefaultAgency } from '../../../selectors';
import { RouteMapGeometryRecord } from '../../../types/gtfsTypes';
import { PolylineProps } from '../../../types/MapModels';
import { AverageWeekdayBoardingSummaryRecord, AverageWeekdayBoardingTimelineRecord, RidershipStopAggRecord, RidershipStopAreaLocation } from '../../../types/ridershipTypes';
import { DayTypeFilter, dayTypeOptions } from '../../../utils/dayType';
import { formatTimelineMonth, getRidershipDateOnlyDaysAgo } from '../../../utils/ridershipDate';
import AzureMapSelectionMap from '../../shared/AzureMap/AzureMapSelectionMap';
import RidershipMapLegend from '../common/RidershipMapLegend';
import { buildBubblePinsFromStopActivity } from '../common/ridershipMapUtils';
import { projectAggregateMetrics, toRidershipCsvInteger } from '../common/ridershipUtils';
import StopPopupContent from '../common/StopPopupContent';
import { useChartLegendIsolation } from '../common/useChartLegendIsolation';

interface Props {
    agency: ReturnType<typeof getSelectedOrDefaultAgency>;
}

interface RouteOption {
    id: string;
    displayName: string;
    routeName?: string;
    serviceType: RouteServiceType;
}

type RouteTypeFilter = 'all' | 'local' | 'parkRide' | 'rail';
type RouteServiceType = 'LOCAL BUS' | 'PARK & RIDE' | 'METRORAIL' | 'UNKNOWN';
export type YoyAggregation = 'sum' | 'avg';

interface RouteSummaryCacheEntry {
    rows: AverageWeekdayBoardingSummaryRecord[];
}

interface RouteTimelineCacheEntry {
    rows: AverageWeekdayBoardingTimelineRecord[];
}

interface SelectedStopInfo {
    stopId?: string;
    stopName?: string;
    routeName?: string;
    boarding?: number;
    alighting?: number;
    load?: number;
    lat?: number;
    lon?: number;
}

const monthOrder = [
    'October',
    'November',
    'December',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
];

const fiscalYearPalette = ['#d35400', '#8e44ad', '#2980b9', '#16a085', '#2c3e50', '#c0392b'];
const routeColorPalette = ['#026db5', '#16a085', '#8e44ad', '#c0392b', '#2c3e50', '#d35400'];
const fiscalYearStart = 2022;

const typeOptions = [
    {
        key: 'all',
        value: 'all',
        text: 'All',
    },
    {
        key: 'local',
        value: 'local',
        text: 'Local',
    },
    {
        key: 'parkRide',
        value: 'parkRide',
        text: 'Park & Ride',
    },
    {
        key: 'rail',
        value: 'rail',
        text: 'Rail',
    },
];

const allRouteOptionsByType: Record<RouteTypeFilter, { id: string; text: string }> = {
    all: {
        id: '__all_routes_all__',
        text: 'All',
    },
    local: {
        id: '__all_routes_local__',
        text: 'All (Local)',
    },
    parkRide: {
        id: '__all_routes_parkride__',
        text: 'All (P&R)',
    },
    rail: {
        id: '__all_routes_rail__',
        text: 'All (Rail)',
    },
};

type RidershipHeatMetric = 'boarding' | 'alighting' | 'load';
const numberFormatterEnUs = new Intl.NumberFormat('en-US');

export function formatRoundedNumberEnUs(value?: number | null) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '0';
    }
    return numberFormatterEnUs.format(Math.round(value));
}

function isAbortError(error: any) {
    return error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
}

export function getEffectiveDayType(dayType: DayTypeFilter, aggregation: YoyAggregation): DayTypeFilter {
    return aggregation === 'sum' ? 'any' : dayType;
}

export function isDayTypeControlDisabled(aggregation: YoyAggregation): boolean {
    return aggregation === 'sum';
}

export function restoreDayTypeForAverage(lastNonAnyDayType: DayTypeFilter): DayTypeFilter {
    return lastNonAnyDayType || 'workdays';
}

function normalizeServiceType(serviceType?: string): RouteServiceType {
    const normalized = (serviceType || '').trim().toUpperCase().replace(/\s+/g, ' ');
    if (normalized === 'LOCAL BUS') {
        return 'LOCAL BUS';
    }
    if (normalized === 'PARK & RIDE' || normalized === 'PARK AND RIDE' || normalized === 'PARK&RIDE') {
        return 'PARK & RIDE';
    }
    if (normalized === 'METRORAIL' || normalized === 'METRO RAIL') {
        return 'METRORAIL';
    }
    return 'UNKNOWN';
}

function matchRouteTypeFilter(serviceType: RouteServiceType, filter: RouteTypeFilter) {
    if (filter === 'all') {
        return true;
    }
    if (filter === 'local') {
        return serviceType === 'LOCAL BUS';
    }
    if (filter === 'parkRide') {
        return serviceType === 'PARK & RIDE';
    }
    return serviceType === 'METRORAIL';
}

export function getAllRouteOption(type: RouteTypeFilter) {
    return allRouteOptionsByType[type];
}

export function getDefaultSelectedRouteIdsForType(type: RouteTypeFilter) {
    return [getAllRouteOption(type).id];
}

export function buildRouteDropdownOptions(routeOptions: RouteOption[], type: RouteTypeFilter) {
    const allOption = getAllRouteOption(type);
    const filtered = routeOptions.filter(option => matchRouteTypeFilter(option.serviceType, type));
    const mapped = filtered.map(option => ({
        key: option.id,
        value: option.id,
        text: option.displayName,
        routeName: option.routeName ?? option.id,
    }));

    return [{
        key: allOption.id,
        value: allOption.id,
        text: allOption.text,
        routeName: allOption.text,
    }, ...mapped];
}

export function normalizeRouteIds(routeIds: string[]) {
    return Array.from(new Set((routeIds || [])
        .map(routeId => (routeId || '').trim())
        .filter(routeId => routeId.length > 0)))
        .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
}

export function buildRouteBatchCacheKey(routeIds: string[], aggregation: YoyAggregation, dayType: DayTypeFilter) {
    const normalizedRouteIds = normalizeRouteIds(routeIds);
    return `${normalizedRouteIds.join(',')}|${aggregation}|${dayType}`;
}

export function buildYoySummaryCacheKey(routeIds: string[]) {
    return buildRouteBatchCacheKey(routeIds, 'sum', 'any');
}

export interface TimelineFiscalSlot {
    fiscalYear: string;
    monthName: string;
    label: string;
    monthDate?: string;
}

export function buildTimelineDataKey(fiscalYear: string, monthName: string) {
    return `${fiscalYear}|${monthName}`;
}

export function buildTimelineFiscalSlots(yearsToRender: string[], fiscalMonthOrder: string[]): TimelineFiscalSlot[] {
    const sortedYears = [...yearsToRender].sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    const useSingleYearLabels = sortedYears.length === 1;
    return sortedYears.flatMap(fiscalYear => fiscalMonthOrder.map(monthName => ({
        fiscalYear,
        monthName,
        label: useSingleYearLabels ? monthName : `${monthName} (${fiscalYear})`,
    })));
}

export function buildTimelineFiscalAxisLabels(slots: TimelineFiscalSlot[]) {
    return slots.map(slot => slot.label);
}

function resolveTimelineRowKey(row: AverageWeekdayBoardingTimelineRecord) {
    const fiscalYear = (row.fiscalYear || '').trim();
    const monthName = (row.monthName || '').trim();
    if (fiscalYear && monthName) {
        return buildTimelineDataKey(fiscalYear, monthName);
    }

    const parsed = moment.utc(row.monthDate);
    if (!parsed.isValid()) {
        return null;
    }

    const derivedFiscalYear = String(parsed.month() + 1 >= 10 ? parsed.year() + 1 : parsed.year());
    const derivedMonthName = parsed.format('MMMM');
    return buildTimelineDataKey(derivedFiscalYear, derivedMonthName);
}

export function normalizeRouteSelection(
    routeIds: string[],
    type: RouteTypeFilter,
    routeDropdownOptions: Array<{ value: string }>,
) {
    const allOptionId = getAllRouteOption(type).id;
    const allowedIds = new Set(routeDropdownOptions.map(option => option.value));
    const normalized = (routeIds || [])
        .filter(id => allowedIds.has(id));

    if (normalized.length === 0) {
        return [];
    }

    const hasAllSelected = normalized.includes(allOptionId);
    if (!hasAllSelected) {
        return Array.from(new Set(normalized));
    }

    const lastSelected = normalized[normalized.length - 1];
    if (lastSelected === allOptionId) {
        return [allOptionId];
    }

    return Array.from(new Set(normalized.filter(id => id !== allOptionId)));
}

export function getEffectiveRouteIdsForApply(
    selectedRouteIds: string[],
    type: RouteTypeFilter,
    routeOptions: RouteOption[],
) {
    const allOptionId = getAllRouteOption(type).id;
    if (selectedRouteIds.includes(allOptionId)) {
        return normalizeRouteIds(routeOptions
            .filter(option => matchRouteTypeFilter(option.serviceType, type))
            .map(option => option.id));
    }

    const routeIdsSet = new Set(routeOptions.map(option => option.id));
    return normalizeRouteIds(selectedRouteIds.filter(routeId => routeIdsSet.has(routeId)));
}

function buildFiscalYearOptions(currentYear: number) {
    const options: Array<{ key: string; value: string; text: string }> = [];
    for (let year = fiscalYearStart; year <= currentYear; year++) {
        const value = year.toString();
        options.push({
            key: value,
            value,
            text: value,
        });
    }
    return options;
}

function buildDefaultFiscalYears(currentYear: number) {
    return buildFiscalYearOptions(currentYear)
        .map(option => option.value);
}

function normalizeColor(color: string | null | undefined, fallback: string) {
    if (!color) return fallback;
    const trimmed = color.trim();
    if (!trimmed) return fallback;
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function buildMapGeometry(routeInfos: RouteMapGeometryRecord[]) {
    const polylines: PolylineProps[] = [];
    const stopLocations: RidershipStopAreaLocation[] = [];
    const seenStops = new Set<string>();

    routeInfos.forEach((routeInfo, index) => {
        const strokeColor = normalizeColor(routeInfo.routeColor, routeColorPalette[index % routeColorPalette.length]);

        for (const direction of routeInfo.directions || []) {
            for (const variant of direction.directionVariants || []) {
                if (variant.shapePoints?.length) {
                    polylines.push({
                        options: {
                            strokeColor,
                            strokeThickness: 3,
                        },
                        locations: variant.shapePoints.map(point => [point.lat as Latitude, point.lon as Longitude]),
                    });
                }

                for (const stop of variant.stops || []) {
                    const lat = stop.lat as Latitude;
                    const lon = stop.lon as Longitude;
                    const stopId = String(stop.stopCode || stop.stopId || `${lat}-${lon}`);
                    const key = stopId;
                    if (seenStops.has(key)) continue;
                    seenStops.add(key);
                    stopLocations.push({
                        stopId,
                        stopName: String(stop.stopName || 'Stop'),
                        stopCode: String(stop.stopCode || stop.stopId || stopId),
                        latitude: lat,
                        longitude: lon,
                    });
                }
            }
        }
    });

    return {
        polylines,
        stopLocations,
    };
}

export const RidershipBoardingSummaryPage: React.FC<Props> = (props: Props) => {
    const [type, setType] = React.useState<RouteTypeFilter>('all');
    const [selectedDayType, setSelectedDayType] = React.useState<DayTypeFilter>('workdays');
    const [appliedDayType, setAppliedDayType] = React.useState<DayTypeFilter>('workdays');
    const [routeOptions, setRouteOptions] = React.useState<RouteOption[]>([]);
    const [selectedRouteIds, setSelectedRouteIds] = React.useState<string[]>([]);
    const [appliedRouteIds, setAppliedRouteIds] = React.useState<string[]>([]);
    const [hasApplied, setHasApplied] = React.useState<boolean>(false);
    const [routeGeometryMap, setRouteGeometryMap] = React.useState<Record<string, RouteMapGeometryRecord | null>>({});
    const [summaryCache, setSummaryCache] = React.useState<Record<string, RouteSummaryCacheEntry>>({});
    const [timelineCache, setTimelineCache] = React.useState<Record<string, RouteTimelineCacheEntry>>({});
    const [selectedFiscalYears, setSelectedFiscalYears] = React.useState<string[]>(() => buildDefaultFiscalYears(new Date().getFullYear()));
    const [appliedFiscalYears, setAppliedFiscalYears] = React.useState<string[]>(() => buildDefaultFiscalYears(new Date().getFullYear()));
    const [routesLoading, setRoutesLoading] = React.useState(false);
    const [summaryLoading, setSummaryLoading] = React.useState(false);
    const [timelineLoading, setTimelineLoading] = React.useState(false);
    const [yoyAggregation, setYoyAggregation] = React.useState<YoyAggregation>('sum');
    const [appliedYoyAggregation, setAppliedYoyAggregation] = React.useState<YoyAggregation>('sum');
    const [lastNonAnyDayType, setLastNonAnyDayType] = React.useState<DayTypeFilter>('workdays');
    const [selectedStop, setSelectedStop] = React.useState<SelectedStopInfo | null>(null);
    const [heatMetric, setHeatMetric] = React.useState<RidershipHeatMetric>('boarding');
    const [mapStopActivityRows, setMapStopActivityRows] = React.useState<RidershipStopAggRecord[]>([]);
    const [mapDataLoading, setMapDataLoading] = React.useState(false);
    const summaryAbortRef = React.useRef<AbortController | null>(null);
    const timelineAbortRef = React.useRef<AbortController | null>(null);
    const mapAbortRef = React.useRef<AbortController | null>(null);
    const yoyCsvLinkRef = React.useRef<any>(null);
    const timelineCsvLinkRef = React.useRef<any>(null);

    const startAbortableRequest = React.useCallback((ref: React.MutableRefObject<AbortController | null>) => {
        if (ref.current) {
            ref.current.abort();
        }
        const controller = new AbortController();
        ref.current = controller;
        return controller;
    }, []);

    const reportLoading = summaryLoading || timelineLoading || mapDataLoading;

    const handleCancel = React.useCallback(() => {
        summaryAbortRef.current?.abort();
        timelineAbortRef.current?.abort();
        mapAbortRef.current?.abort();
        setSummaryLoading(false);
        setTimelineLoading(false);
        setMapDataLoading(false);
    }, []);

    const agencyId = props.agency?.id;
    const currentFiscalYear = new Date().getFullYear();
    const fiscalYearOptions = React.useMemo(() => buildFiscalYearOptions(currentFiscalYear), [currentFiscalYear]);
    const defaultFiscalYears = React.useMemo(() => buildDefaultFiscalYears(currentFiscalYear), [currentFiscalYear]);
    const effectiveSelectedDayType = React.useMemo(() => getEffectiveDayType(selectedDayType, yoyAggregation), [selectedDayType, yoyAggregation]);
    const effectiveAppliedDayType = React.useMemo(() => getEffectiveDayType(appliedDayType, appliedYoyAggregation), [appliedDayType, appliedYoyAggregation]);
    const effectiveSelectedRouteIds = React.useMemo(() => getEffectiveRouteIdsForApply(selectedRouteIds, type, routeOptions), [selectedRouteIds, type, routeOptions]);
    const appliedRouteCacheKey = React.useMemo(() => buildRouteBatchCacheKey(appliedRouteIds, appliedYoyAggregation, effectiveAppliedDayType), [appliedRouteIds, appliedYoyAggregation, effectiveAppliedDayType]);
    const yoySummaryCacheKey = React.useMemo(() => buildYoySummaryCacheKey(appliedRouteIds), [appliedRouteIds]);

    const routeDropdownOptions = React.useMemo(() => buildRouteDropdownOptions(routeOptions, type), [routeOptions, type]);

    React.useEffect(() => {
        if (!agencyId) {
            setRouteOptions([]);
            setSelectedRouteIds([]);
            setAppliedRouteIds([]);
            setSelectedDayType('workdays');
            setAppliedDayType('workdays');
            setAppliedFiscalYears(defaultFiscalYears);
            setHasApplied(false);
            setRouteGeometryMap({});
            setSummaryCache({});
            setTimelineCache({});
            setSelectedFiscalYears(defaultFiscalYears);
            setLastNonAnyDayType('workdays');
            setMapStopActivityRows([]);
            setMapDataLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setRoutesLoading(true);
                const pairs = await getRoutesPairsWithLongName(agencyId);
                if (cancelled) return;
                const options = (pairs || []).map(p => ({
                    id: p.routeId,
                    displayName: [p.routeName, p.longName].filter(Boolean).join(' - ').trim() || p.routeName,
                    routeName: p.routeName,
                    serviceType: normalizeServiceType(p.serviceType),
                }));
                setRouteOptions(options);
                setSelectedDayType('workdays');
                setAppliedDayType('workdays');
                setRouteGeometryMap({});
                setSummaryCache({});
                setTimelineCache({});
                setSelectedFiscalYears(defaultFiscalYears);
                setLastNonAnyDayType('workdays');
                setAppliedRouteIds([]);
                setAppliedFiscalYears(defaultFiscalYears);
                setHasApplied(false);
            } finally {
                if (!cancelled) setRoutesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [agencyId, defaultFiscalYears]);

    // Reset selected routes to type-scoped All whenever Type changes.
    React.useEffect(() => {
        const defaultSelection = getDefaultSelectedRouteIdsForType(type);
        setSelectedRouteIds(prev => {
            if (prev.length === 1 && prev[0] === defaultSelection[0]) {
                return prev;
            }
            return defaultSelection;
        });
    }, [type, agencyId]);

    React.useEffect(() => {
        if (!appliedRouteIds.length || !hasApplied) return;
        const missing = appliedRouteIds.filter(routeId =>
            typeof routeGeometryMap[routeId] === 'undefined');
        if (!missing.length) return;
        let cancelled = false;
        (async () => {
            try {
                const rows = await getRouteMapGeometryByRoutes(missing);
                if (cancelled) {
                    return;
                }

                const geometryByRouteId = new Map<string, RouteMapGeometryRecord>();
                for (const row of rows || []) {
                    const routeId = (row?.routeId || '').trim();
                    if (!routeId) {
                        continue;
                    }

                    geometryByRouteId.set(routeId.toLowerCase(), row);
                }

                setRouteGeometryMap(prev => {
                    const next = { ...prev };
                    for (const routeId of missing) {
                        if (typeof next[routeId] !== 'undefined') {
                            continue;
                        }

                        const key = routeId.toLowerCase();
                        next[routeId] = geometryByRouteId.get(key) ?? null;
                    }

                    return next;
                });
            } catch {
                if (!cancelled) {
                    setRouteGeometryMap(prev => {
                        const next = { ...prev };
                        for (const routeId of missing) {
                            if (typeof next[routeId] !== 'undefined') {
                                continue;
                            }

                            next[routeId] = null;
                        }

                        return next;
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [appliedRouteIds, hasApplied, routeGeometryMap]);

    React.useEffect(() => {
        if (!agencyId || !hasApplied) {
            if (Object.keys(summaryCache).length > 0) {
                setSummaryCache({});
            }
            return;
        }

        if (appliedRouteIds.length === 0 || summaryCache[yoySummaryCacheKey]) {
            return;
        }

        let cancelled = false;
        const controller = startAbortableRequest(summaryAbortRef);
        (async () => {
            try {
                setSummaryLoading(true);
                const rows = await getAverageWeekdayBoardingSummary(agencyId, appliedRouteIds, 'any', 'sum', { signal: controller.signal });
                if (!cancelled && !controller.signal.aborted) {
                    setSummaryCache(prev => {
                        return {
                            ...prev,
                            [yoySummaryCacheKey]: {
                                rows: rows || [],
                            },
                        };
                    });
                }
            } catch (error) {
                if (isAbortError(error) || cancelled || controller.signal.aborted) {
                    return;
                }
                throw error;
            } finally {
                if (!cancelled) setSummaryLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [agencyId, appliedRouteIds, summaryCache, yoySummaryCacheKey, startAbortableRequest, hasApplied]);

    React.useEffect(() => {
        if (!agencyId || !hasApplied) {
            if (Object.keys(timelineCache).length > 0) {
                setTimelineCache({});
            }
            return;
        }

        if (appliedRouteIds.length === 0 || timelineCache[appliedRouteCacheKey]) {
            return;
        }

        let cancelled = false;
        const controller = startAbortableRequest(timelineAbortRef);
        (async () => {
            try {
                setTimelineLoading(true);
                const rows = await getAverageWeekdayBoardingTimeline(agencyId, appliedRouteIds, effectiveAppliedDayType, appliedYoyAggregation, { signal: controller.signal });
                if (!cancelled && !controller.signal.aborted) {
                    setTimelineCache(prev => {
                        return {
                            ...prev,
                            [appliedRouteCacheKey]: {
                                rows: rows || [],
                            },
                        };
                    });
                }
            } catch (error) {
                if (isAbortError(error) || cancelled || controller.signal.aborted) {
                    return;
                }
                throw error;
            } finally {
                if (!cancelled) setTimelineLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [agencyId, appliedRouteIds, timelineCache, appliedRouteCacheKey, appliedYoyAggregation, effectiveAppliedDayType, startAbortableRequest, hasApplied]);

    const combinedSummaryRows = React.useMemo(() => {
        const entry = summaryCache[yoySummaryCacheKey];
        return entry?.rows || [];
    }, [summaryCache, yoySummaryCacheKey]);

    React.useEffect(() => {
        const availableFiscalYears = new Set(fiscalYearOptions.map(option => String(option.value)));
        setSelectedFiscalYears(prev => {
            const filtered = prev.filter(fy => availableFiscalYears.has(fy));
            if (filtered.length === prev.length && filtered.length > 0) {
                return prev;
            }
            return filtered.length > 0 ? filtered : defaultFiscalYears;
        });
        setAppliedFiscalYears(prev => {
            const filtered = prev.filter(fy => availableFiscalYears.has(fy));
            if (filtered.length === prev.length && filtered.length > 0) {
                return prev;
            }
            return filtered.length > 0 ? filtered : defaultFiscalYears;
        });
    }, [fiscalYearOptions, defaultFiscalYears]);

    // Fiscal Year is a pure client-side display filter: apply immediately without API refetch.
    React.useEffect(() => {
        setAppliedFiscalYears(selectedFiscalYears);
    }, [selectedFiscalYears]);

    React.useEffect(() => {
        setAppliedYoyAggregation(yoyAggregation);
    }, [yoyAggregation]);

    React.useEffect(() => {
        setSelectedStop(null);
    }, [appliedRouteIds, agencyId]);

    const mapGeometry = React.useMemo(() => {
        const infos = appliedRouteIds
            .map(routeId => routeGeometryMap[routeId])
            .filter((info): info is RouteMapGeometryRecord => !!info);
        return buildMapGeometry(infos);
    }, [appliedRouteIds, routeGeometryMap]);

    const mapRouteIds = React.useMemo(() => normalizeRouteIds(appliedRouteIds), [appliedRouteIds]);
    const mapLoading = React.useMemo(() => (
        appliedRouteIds.some(routeId => typeof routeGeometryMap[routeId] === 'undefined')
    ), [appliedRouteIds, routeGeometryMap]);

    React.useEffect(() => {
        if (!agencyId || !hasApplied || mapRouteIds.length === 0) {
            setMapStopActivityRows([]);
            setMapDataLoading(false);
            return;
        }

        let disposed = false;
        let activeController: AbortController | null = null;

        const loadMapStopActivity = async (controller: AbortController, fromDate: string, toDate: string) => {
            try {
                setMapDataLoading(true);
                const rows = await getRidershipStopActivityByRoutes(
                    agencyId,
                    mapRouteIds,
                    fromDate,
                    toDate,
                    effectiveAppliedDayType,
                    undefined,
                    undefined,
                    { signal: controller.signal },
                );
                if (!disposed && !controller.signal.aborted) {
                    setMapStopActivityRows(rows || []);
                }
            } catch (error) {
                if (isAbortError(error) || disposed || controller.signal.aborted) {
                    return;
                }
                if (!disposed) {
                    setMapStopActivityRows([]);
                }
            } finally {
                if (!disposed) {
                    setMapDataLoading(false);
                }
            }
        };

        // Delay by one tick to avoid duplicate network requests under React StrictMode dev re-mount cycle.
        const startTimerId = window.setTimeout(() => {
            if (disposed) {
                return;
            }
            const controller = startAbortableRequest(mapAbortRef);
            activeController = controller;

            const lastThirtyDaysFromDate = getRidershipDateOnlyDaysAgo(30);
            const lastThirtyDaysToDate = getRidershipDateOnlyDaysAgo(0);
            void loadMapStopActivity(controller, lastThirtyDaysFromDate, lastThirtyDaysToDate);
        }, 0);

        return () => {
            disposed = true;
            window.clearTimeout(startTimerId);
            activeController?.abort();
        };
    }, [agencyId, hasApplied, effectiveAppliedDayType, mapRouteIds, startAbortableRequest]);

    const mapRowsForView = React.useMemo(() => {
        return projectAggregateMetrics(mapStopActivityRows, appliedYoyAggregation) as RidershipStopAggRecord[];
    }, [mapStopActivityRows, appliedYoyAggregation]);

    const mapBubblePins = React.useMemo(() => {
        return buildBubblePinsFromStopActivity(mapGeometry.stopLocations, mapRowsForView);
    }, [mapGeometry.stopLocations, mapRowsForView]);

    const totalStopsCount = React.useMemo(() => {
        return mapGeometry.stopLocations.length;
    }, [mapGeometry.stopLocations.length]);

    const yoyLegend = useChartLegendIsolation(appliedFiscalYears);
    const timelineLegend = useChartLegendIsolation(appliedRouteCacheKey);

    const yoyChartData = React.useMemo(() => {
        const sumMap = new Map<string, number>();
        for (const row of combinedSummaryRows) {
            const type = (row.type || '').toString().toLowerCase();
            if (type === 'average') {
                continue;
            }
            const key = `${row.fiscalYear}|${row.monthName}`;
            sumMap.set(key, (sumMap.get(key) || 0) + row.boarding);
        }

        const labels = monthOrder;
        const yearsToRender = appliedFiscalYears.length ? appliedFiscalYears : fiscalYearOptions.map(option => String(option.value));
        const datasets = yearsToRender.map((fy, index) => {
            const color = fiscalYearPalette[index % fiscalYearPalette.length];
            const data = labels.map(month => {
                const key = `${fy}|${month}`;
                const total = sumMap.get(key);
                return typeof total === 'number' ? total : null;
            });
            return {
                label: fy,
                data,
                borderColor: color,
                backgroundColor: color,
                fill: false,
                tension: 0.25,
                pointRadius: 4,
                pointHoverRadius: 6,
                hidden: yoyLegend.isDatasetHidden(String(fy)),
            };
        });
        return {
            labels,
            datasets,
        };
    }, [combinedSummaryRows, appliedFiscalYears, fiscalYearOptions, yoyLegend.isDatasetHidden]);

    const timelineData = React.useMemo(() => {
        const yearsToRender = appliedFiscalYears.length ? appliedFiscalYears : fiscalYearOptions.map(option => String(option.value));
        const fiscalYearSet = new Set(yearsToRender);
        const timelineRows = timelineCache[appliedRouteCacheKey]?.rows || [];
        const slots = buildTimelineFiscalSlots(yearsToRender, monthOrder);
        const monthDateByKey = new Map<string, string>();
        const perRouteMaps: { routeId: string; label: string; color: string; map: Map<string, number> }[] = [];
        const rowsByRouteShortName = timelineRows.reduce((acc, row) => {
            const key = (row.routeShortName || '').trim().toLowerCase();
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(row);
            return acc;
        }, {} as Record<string, AverageWeekdayBoardingTimelineRecord[]>);

        appliedRouteIds.forEach((routeId, idx) => {
            const option = routeOptions.find(o => o.id === routeId);
            const routeShortName = (option?.routeName || routeId || '').trim().toLowerCase();
            const rows = rowsByRouteShortName[routeShortName] || [];
            const map = new Map<string, number>();
            for (const row of rows) {
                const dataKey = resolveTimelineRowKey(row);
                if (!dataKey) {
                    continue;
                }

                const rowFiscalYear = (row.fiscalYear || '').trim() || dataKey.split('|')[0];
                if (!fiscalYearSet.has(rowFiscalYear)) {
                    continue;
                }

                map.set(dataKey, row.boarding);
                monthDateByKey.set(dataKey, row.monthDate);
            }
            const label = option?.displayName || routeId;
            const color = routeColorPalette[idx % routeColorPalette.length];
            perRouteMaps.push({
                routeId,
                label,
                color,
                map,
            });
        });

        const timelineSlots = slots.map(slot => ({
            ...slot,
            monthDate: monthDateByKey.get(buildTimelineDataKey(slot.fiscalYear, slot.monthName)),
        }));
        const labels = buildTimelineFiscalAxisLabels(timelineSlots);

        const datasets = perRouteMaps.map((routeSeries) => ({
            label: routeSeries.label,
            data: timelineSlots.map(slot => {
                const value = routeSeries.map.get(buildTimelineDataKey(slot.fiscalYear, slot.monthName));
                return typeof value === 'number' ? value : null;
            }),
            borderColor: routeSeries.color,
            backgroundColor: routeSeries.color,
            fill: false,
            tension: 0.15,
            pointRadius: 3,
            pointHoverRadius: 5,
            hidden: timelineLegend.isDatasetHidden(routeSeries.label),
        }));

        return {
            chartData: {
                labels,
                datasets,
            } as any,
            timelineSlots,
        };
    }, [appliedRouteIds, timelineCache, routeOptions, appliedRouteCacheKey, appliedFiscalYears, fiscalYearOptions, timelineLegend.isDatasetHidden]);
    const timelineChartData = timelineData.chartData;
    const timelineSlots = timelineData.timelineSlots;

    const mapBlocking = routesLoading || (hasApplied && (mapLoading || mapDataLoading));
    const yoyBlocking = routesLoading || (hasApplied && summaryLoading);
    const timelineBlocking = timelineLoading;

    const yoyCsvHeaders = React.useMemo(() => {
        const seriesHeaders = (yoyChartData.datasets || []).map((dataset, index) => ({
            label: String(dataset.label ?? `Series ${index + 1}`),
            key: `series_${index}`,
        }));
        return [
            {
                label: 'Month',
                key: 'month',
            },
            ...seriesHeaders,
        ];
    }, [yoyChartData.datasets]);

    const yoyCsvRows = React.useMemo(() => {
        const labels = yoyChartData.labels || [];
        if (!labels.length) return [];
        return labels.map((label, rowIndex) => {
            const row: Record<string, string | number | null> = { month: String(label ?? '') };
            (yoyChartData.datasets || []).forEach((dataset, datasetIndex) => {
                const data = dataset.data as Array<number | null | undefined>;
                row[`series_${datasetIndex}`] = toRidershipCsvInteger(data?.[rowIndex] ?? undefined);
            });
            return row;
        });
    }, [yoyChartData.datasets]);

    const timelineCsvHeaders = React.useMemo(() => {
        const seriesHeaders = (timelineChartData.datasets || []).map((dataset: { label: any; }, index: number) => ({
            label: String(dataset.label ?? `Series ${index + 1}`),
            key: `series_${index}`,
        }));
        return [
            {
                label: 'Month',
                key: 'month',
            },
            ...seriesHeaders,
        ];
    }, [timelineChartData.datasets]);

    const timelineCsvRows = React.useMemo(() => {
        if (!timelineSlots.length) return [];
        return timelineSlots.map((slot, rowIndex) => {
            const row: Record<string, string | number | null> = { month: slot.label };
            (timelineChartData.datasets || []).forEach((dataset: {
                data: (number | null | undefined)[];
            }, datasetIndex: any) => {
                const data = dataset.data as Array<number | null | undefined>;
                row[`series_${datasetIndex}`] = toRidershipCsvInteger(data?.[rowIndex] ?? undefined);
            });
            return row;
        });
    }, [timelineSlots, timelineChartData.datasets]);

    const yoyCsvFileName = React.useMemo(() => {
        const timestamp = moment(new Date()).format('YYYY_MM_DD');
        return `average_weekday_boardings_yoy_${timestamp}.csv`;
    }, []);

    const timelineCsvFileName = React.useMemo(() => {
        const timestamp = moment(new Date()).format('YYYY_MM_DD');
        return `average_weekday_boardings_timeline_${timestamp}.csv`;
    }, []);

    const yoyCsvDisabled = !yoyChartData.datasets.length || yoyBlocking;
    const timelineCsvDisabled = !timelineCsvRows.length || timelineBlocking;

    const chartOptionsYoY: ChartOptions<'line'> = React.useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'bottom' as const,
                align: 'center' as const,
                onClick: yoyLegend.handleLegendClick,
            },
            datalabels: { display: false },
            tooltip: {
                mode: 'x' as const,
                intersect: false,
                caretPadding: 12,
                titleFont: { size: 16 },
                bodyFont: { size: 15 },
                callbacks: {
                    title: function (tooltipItem: TooltipItem<'line'>[]) {
                        return tooltipItem[0].label;
                    },
                    label: function (tooltipItem: any) {
                        const dataset = tooltipItem.dataset || {};
                        const datasetLabel = dataset.label || 'Value';
                        const y = tooltipItem?.parsed?.y;
                        const formatted = formatRoundedNumberEnUs(typeof y === 'number' ? y : undefined);
                        return ` ${datasetLabel}: ${formatted}`;
                    },
                    labelColor: function (tooltipItem: any) {
                        const dataset = tooltipItem.dataset || {};
                        const rawBorder = dataset.borderColor;
                        const rawBackground = dataset.backgroundColor;
                        const borderColor = typeof rawBorder === 'string' ? rawBorder : '#34495EFF';
                        const backgroundColor = typeof rawBackground === 'string' ? rawBackground : borderColor;
                        return {
                            borderColor,
                            backgroundColor,
                        } as any;
                    },
                },
            },
        },
        scales: {
            x: { ticks: { autoSkip: false } },
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => formatRoundedNumberEnUs(typeof value === 'number' ? value : Number(value)),
                },
            },
        },
    }), [yoyLegend.handleLegendClick]);

    const chartOptionsTimeline: ChartOptions<'line'> = React.useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => formatRoundedNumberEnUs(typeof value === 'number' ? value : Number(value)),
                },
            },
            x: {
                ticks: {
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                },
            },
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom' as const,
                align: 'center' as const,
                onClick: timelineLegend.handleLegendClick,
            },
            datalabels: { display: false },
            tooltip: {
                mode: 'x' as const,
                intersect: false,
                caretPadding: 12,
                titleFont: { size: 16 },
                bodyFont: { size: 15 },
                callbacks: {
                    title: function (tooltipItem: TooltipItem<'line'>[]) {
                        const slot = timelineSlots[tooltipItem[0].dataIndex];
                        if (!slot) {
                            return '';
                        }
                        if (slot.monthDate) {
                            return `${slot.label} (${formatTimelineMonth(slot.monthDate)})`;
                        }
                        return slot.label;
                    },
                    label: function (tooltipItem: any) {
                        const dataset = tooltipItem.dataset || {};
                        const datasetLabel = dataset.label || 'Value';
                        const y = tooltipItem?.parsed?.y;
                        const formatted = formatRoundedNumberEnUs(typeof y === 'number' ? y : undefined);
                        return ` ${datasetLabel}: ${formatted}`;
                    },
                    labelColor: function (tooltipItem: any) {
                        const dataset = tooltipItem.dataset || {};
                        const rawBorder = dataset.borderColor;
                        const rawBackground = dataset.backgroundColor;
                        const borderColor = typeof rawBorder === 'string' ? rawBorder : '#34495EFF';
                        const backgroundColor = typeof rawBackground === 'string' ? rawBackground : borderColor;
                        return {
                            borderColor,
                            backgroundColor,
                        } as any;
                    },
                },
            },
        },
    }), [timelineSlots, timelineLegend.handleLegendClick]);

    const handleStopClick = React.useCallback((info: any) => {
        setSelectedStop({
            stopId: info?.stopId,
            stopName: info?.stopName || info?.title,
            routeName: info?.routeName,
            boarding: typeof info?.boarding === 'number' ? info.boarding : undefined,
            alighting: typeof info?.alighting === 'number' ? info.alighting : undefined,
            load: typeof info?.load === 'number' ? info.load : undefined,
            lat: info.lat,
            lon: info.lon,
        });
    }, []);

    const handleApply = React.useCallback(() => {
        setAppliedRouteIds(effectiveSelectedRouteIds);
        setAppliedDayType(effectiveSelectedDayType);
        setMapStopActivityRows([]);
        setHasApplied(true);
    }, [effectiveSelectedRouteIds, effectiveSelectedDayType]);

    const popupInfoboxes = React.useMemo(() => {
        if (!selectedStop) {
            return [];
        }
        const title = selectedStop.stopName && selectedStop.stopId
            ? `${selectedStop.stopName} (${selectedStop.stopId})`
            : (selectedStop.stopName || selectedStop.stopId || 'Stop');
        return [{
            location: [selectedStop.lat, selectedStop.lon] as [number, number],
            title,
            text: (
                <StopPopupContent
                    stopId={selectedStop.stopId}
                    stopName={selectedStop.stopName}
                    routeName={selectedStop.routeName}
                    boarding={selectedStop.boarding}
                    alighting={selectedStop.alighting}
                    load={selectedStop.load}
                    formatNumber={(value) => formatRoundedNumberEnUs(typeof value === 'number' ? value : undefined)}
                />
            ),
            options: { showCloseButton: true, pixelOffset: [0, -15] },
        }];
    }, [selectedStop]);

    return (
        <Segment basic>
            <Header as="h1" className="reportHeader">Ridership Boarding Summary</Header>
            <Form>
                <Form.Group widths="equal">
                    <Form.Field>
                        <label className="inputLabel">Type</label>
                        <Dropdown className="ui dropdown"
                                  selection
                                  options={typeOptions}
                                  value={type}
                                  onChange={(_, data) => setType((data.value as RouteTypeFilter) || 'all')}/>
                    </Form.Field>
                    <Form.Field>
                        <label className="inputLabel">Routes</label>
                        <Dropdown
                            className="ui dropdown"
                            placeholder="Select routes"
                            multiple
                            fluid
                            search
                            selection
                            options={routeDropdownOptions}
                            value={selectedRouteIds}
                            loading={routesLoading}
                            disabled={routesLoading || routeOptions.length === 0}
                            onChange={(_, data) => {
                                const nextValues = (data.value as string[]) || [];
                                setSelectedRouteIds(normalizeRouteSelection(nextValues, type, routeDropdownOptions));
                            }}
                            renderLabel={item => {
                                const option = routeOptions.find(o => o.id === item.value);
                                const label = option?.routeName || item.text || item.value as string;
                                return <Label color="blue" content={label} key={item.key as string}/>;
                            }}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label className="inputLabel">Day Type</label>
                        <Dropdown
                            className="ui dropdown"
                            selection
                            options={dayTypeOptions}
                            value={effectiveSelectedDayType}
                            disabled={isDayTypeControlDisabled(yoyAggregation)}
                            onChange={(_, data) => {
                                const dayType = (data.value as DayTypeFilter) || 'workdays';
                                setSelectedDayType(dayType);
                                if (dayType !== 'any') {
                                    setLastNonAnyDayType(dayType);
                                }
                            }}
                        />
                    </Form.Field>
                    <Form.Field>
                        {reportLoading
                            ? (
                                <Button className="fieldControl"
                                        color="red"
                                        content="Cancel"
                                        onClick={handleCancel}/>
                            )
                            : (
                                <Button className="primaryButton fieldControl"
                                        content="Apply"
                                        onClick={handleApply}
                                        disabled={routesLoading || effectiveSelectedRouteIds.length === 0}/>
                            )}
                    </Form.Field>
                </Form.Group>
                <Form.Group>
                    <Form.Field style={{ minWidth: 320 }}>
                        <label className="inputLabel">Fiscal Year</label>
                        <Dropdown
                            className="ui dropdown"
                            placeholder="Select Fiscal Years"
                            multiple
                            selection
                            options={fiscalYearOptions}
                            value={selectedFiscalYears}
                            onChange={(_, data) => setSelectedFiscalYears((data.value as string[]) || [])}
                            renderLabel={item => (
                                <Label color="blue" content={item.text} key={item.key as string}/>
                            )}
                        />
                    </Form.Field>
                    <Form.Field style={{ minWidth: 220 }}>
                        <label className="inputLabel">YoY Aggregation</label>
                        <Dropdown
                            className="ui dropdown"
                            selection
                            options={[{
                                key: 'sum',
                                value: 'sum',
                                text: 'Sum',
                            }, {
                                key: 'avg',
                                value: 'avg',
                                text: 'Average',
                            }]}
                            value={yoyAggregation}
                            onChange={(_, data) => {
                                const aggregation = (data.value as YoyAggregation) || 'sum';
                                setYoyAggregation(aggregation);
                                if (aggregation === 'avg') {
                                    setSelectedDayType(restoreDayTypeForAverage(lastNonAnyDayType));
                                }
                            }}
                        />
                    </Form.Field>
                </Form.Group>
            </Form>

            <BlockUi blocking={routesLoading} message="Loading routes...">
                <Grid stackable columns={2}>
                    <Grid.Row>
                        <Grid.Column width={6}>
                            <BlockUi blocking={mapBlocking} message="Loading map data...">
                                <div style={{
                                    width: '100%',
                                    height: 830,
                                    position: 'relative',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8, gap: 8 }}>
                                        <Form size="small" style={{ margin: 0 }}>
                                            <Form.Field style={{ margin: 0, minWidth: 280 }}>
                                                <label className="inputLabel">Statistics for the last 30 days</label>
                                                <Dropdown
                                                    className="ui dropdown heat-metric-dropdown"
                                                    selection
                                                    fluid
                                                    options={[
                                                        { key: 'boarding', value: 'boarding', text: 'Boardings' },
                                                        { key: 'alighting', value: 'alighting', text: 'Alightings' },
                                                    ]}
                                                    value={heatMetric}
                                                    onChange={(_, d) => setHeatMetric((d.value as RidershipHeatMetric) || 'boarding')}
                                                />
                                            </Form.Field>
                                        </Form>
                                    </div>
                                    <AzureMapSelectionMap
                                        mapMode="heat"
                                        heatMetric={heatMetric}
                                        polylinesProp={mapGeometry.polylines}
                                        loadBubblePins={mapBubblePins.loadPins}
                                        alightingsBubblePins={mapBubblePins.alightPins}
                                        boardBubblePins={mapBubblePins.boardPins}
                                        onStopClick={handleStopClick}
                                        infoboxesProp={popupInfoboxes}
                                        showZoomControls={true}
                                    />
                                    <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <RidershipMapLegend mapMode="heat" heatMetric={heatMetric}/>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <Label color="blue">Stops: {numberFormatterEnUs.format(totalStopsCount)}</Label>
                                        </div>
                                    </div>
                                </div>
                            </BlockUi>
                        </Grid.Column>

                        <Grid.Column width={10}>
                            <Segment>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                    <Header as="h3">Year Over Year Stacked Ridership Report</Header>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}>
                                        <CSVLink
                                            data={yoyCsvRows}
                                            headers={yoyCsvHeaders}
                                            filename={yoyCsvFileName}
                                            target="_blank"
                                            className="hidden"
                                            ref={yoyCsvLinkRef as any}
                                        />
                                        <Button
                                            basic
                                            color="grey"
                                            size="small"
                                            icon
                                            labelPosition="left"
                                            loading={yoyBlocking}
                                            disabled={yoyCsvDisabled}
                                            onClick={() => {
                                                yoyCsvLinkRef.current?.link?.click();
                                            }}
                                        >
                                            <Icon name="download"/>
                                            Download CSV
                                        </Button>
                                    </div>
                                </div>
                                <BlockUi blocking={yoyBlocking} message="Loading YoY chart..." style={{
                                    height: 380,
                                }}>
                                    <Line data={yoyChartData as any} options={chartOptionsYoY}
                                          plugins={[ChartDataLabels]}/>

                                </BlockUi>
                            </Segment>
                            <Segment>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                    <Header as="h3">Timeline of Route Ridership Trends</Header>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}>
                                        <CSVLink
                                            data={timelineCsvRows}
                                            headers={timelineCsvHeaders}
                                            filename={timelineCsvFileName}
                                            target="_blank"
                                            className="hidden"
                                            ref={timelineCsvLinkRef as any}
                                        />
                                        <Button
                                            basic
                                            color="grey"
                                            size="small"
                                            icon
                                            labelPosition="left"
                                            loading={yoyBlocking}
                                            disabled={timelineCsvDisabled}
                                            onClick={() => {
                                                timelineCsvLinkRef.current?.link?.click();
                                            }}
                                        >
                                            <Icon name="download"/>
                                            Download CSV
                                        </Button>
                                    </div>
                                </div>
                                <BlockUi blocking={timelineBlocking} message="Loading timeline chart..." style={{
                                    height: 380,
                                }}>
                                    <Line data={timelineChartData as any} options={chartOptionsTimeline}
                                          plugins={[ChartDataLabels]}/>
                                </BlockUi>
                            </Segment>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </BlockUi>
        </Segment>
    );
};

function mapStateToProps(state: AppState): Props {
    return {
        agency: getSelectedOrDefaultAgency(state),
    } as Props;
}

export default connect(mapStateToProps)(RidershipBoardingSummaryPage);
